import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { audiencesService } from "../../services/audiences";
import { audiencesApi } from "../../redux/api/audiencesApi";
import OrbitalLoader from "../../components/common/OrbitalLoader";

const POLL_INTERVAL_MS = 3000;
const FLASH_DURATION_MS = 1000;

const getDisplayStats = (s) => {
  const ms = s?.modal_stats;
  return {
    possible_creators: ms?.creators?.possible ?? 0,
    possible_videos: ms?.videos?.possible ?? 0,
    processed_creators: ms?.creators?.processed ?? 0,
    processed_videos: ms?.videos?.processed ?? 0,
    scored_creators: ms?.creators?.scored ?? 0,
    scored_videos: ms?.videos?.scored ?? 0,
    highfit_creators: ms?.creators?.high_fit ?? 0,
    highfit_videos: ms?.videos?.high_fit ?? 0,
  };
};

const PopulatingModal = ({ variant = "create", existing = false }) => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projectHash = useSelector((state) => state.project.currentProject?.hash);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flashingStats, setFlashingStats] = useState({});
  const isFirstCallRef = useRef(true);
  const timeoutRef = useRef(null);
  const prevStatsRef = useRef({});
  const flashTimersRef = useRef({});

  useEffect(() => {
    if (!hash) {
      navigate("/research/");
      return;
    }

    let cancelled = false;

    const pollStatus = () => {
      audiencesService.getPopulationStatus(
        hash,
        (response) => {
          if (cancelled) return;

          const newData = response.data;
          const newStats = getDisplayStats(newData);

          if (!isFirstCallRef.current) {
            const changed = Object.keys(newStats).filter(
              (key) =>
                prevStatsRef.current[key] !== undefined &&
                prevStatsRef.current[key] !== newStats[key]
            );

            if (changed.length > 0) {
              setFlashingStats((prev) => {
                const next = { ...prev };
                changed.forEach((k) => { next[k] = true; });
                return next;
              });

              changed.forEach((key) => {
                if (flashTimersRef.current[key]) clearTimeout(flashTimersRef.current[key]);
                flashTimersRef.current[key] = setTimeout(() => {
                  setFlashingStats((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                  });
                }, FLASH_DURATION_MS);
              });
            }
          }

          prevStatsRef.current = newStats;
          setStatus(newData);
          setLoading(false);
          isFirstCallRef.current = false;

          const done =
            newData.status === "completed" ||
            newData.status === "failed";

          if (!done) {
            timeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
          }
        },
        () => {
          if (cancelled) return;
          setLoading(false);
        }
      );
    };

    pollStatus();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      Object.values(flashTimersRef.current).forEach(clearTimeout);
    };
  }, [hash, navigate]);

  const audienceUrl = `/audience/${hash}/`;

  const invalidateAudiencesListCache = useCallback(() => {
    if (projectHash) {
      dispatch(
        audiencesApi.util.invalidateTags([{ type: "Audiences", id: projectHash }])
      );
    }
  }, [dispatch, projectHash]);

  const getStatusText = () => {
    if (!status) return "Initializing...";

    switch (status.status) {
      case "pending":
        return "Preparing...";
      case "fetching_creators":
        return "Finding creators in this category...";
      case "fetching_details":
        return "Collecting creator details from orbit...";
      case "fetching_videos":
        return "Pulling creator videos into orbit...";
      case "analyzing_fit":
        return "Detecting high-fit creator signals...";
      case "analyzing_videos":
        return "Reading video signals...";
      case "assessing_video_fit":
        return "Scoring video category fit...";
      case "completed":
        return variant === "sync"
          ? "Audience sync complete!"
          : "Audience population complete!";
      case "failed":
        return "MoonScroll run failed. You can still view partial results.";
      default:
        return "Moonscrolling...";
    }
  };

  const getProgressPercentage = () => {
    if (!status || status.status === "failed") return 0;
    if (status.status === "completed") return 100;
    const p = status.progress_percent;
    return typeof p === "number" && !Number.isNaN(p) ? Math.min(100, Math.max(0, p)) : 0;
  };

  const isCompleted = status?.status === "completed";
  const hasError = status?.status === "failed";
  const baseTitle = status?.audience_name || status?.category_name || "Audience";
  const titleName = status?.category_emoji
    ? `${status.category_emoji} ${baseTitle}`.trim()
    : baseTitle;
  const relevantVideos = status?.modal_stats?.videos?.high_fit ?? 0;
  const customCreatorCount = Number(status?.custom_creator_count ?? 0);
  const partialByVideos =
    relevantVideos >= 10 && relevantVideos < 100;
  const partialByCustomRoster = customCreatorCount >= 1;
  const canViewPartial =
    !isCompleted &&
    !hasError &&
    !existing &&
    (partialByVideos || partialByCustomRoster);
  const canViewFull = isCompleted || hasError || relevantVideos >= 100;

  const fc = (key) => flashingStats[key] ? "stat-flash" : "";
  const stats = getDisplayStats(status);

  return (
    <>
      <style>{`
        @keyframes statFlash {
          0%, 100% { background-color: transparent; }
          25%, 75% { background-color: color-mix(in srgb, var(--theme-primary) 18%, transparent); }
        }
        .stat-flash {
          animation: statFlash ${FLASH_DURATION_MS}ms ease-in-out both;
        }
      `}</style>

      <h2 className="text-2xl font-semibold text-theme-text mb-6">
        {variant === "sync" ? "Syncing audience: " : "Creating Audience: "}
        {titleName}
      </h2>

      {/* Orbital Loader — above table, below title */}
      {!isCompleted && !hasError && (
        <div className="flex justify-center mb-6">
          <OrbitalLoader size={176} special text={getStatusText()} />
        </div>
      )}

      {/* Success Icon */}
      {isCompleted && (
        <div className="flex flex-col items-center mb-6 gap-3">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-lg text-theme-text">{getStatusText()}</p>
        </div>
      )}

      {/* Progress Bar */}
      <div className="w-full bg-theme-secondary rounded-full h-3 mb-4">
        <div
          className="bg-theme-primary h-3 rounded-full transition-all duration-500"
          style={{ width: `${getProgressPercentage()}%` }}
        />
      </div>

      {/* Progress table */}
      {status && (
        <div className="mb-6 overflow-hidden rounded-lg border border-theme-secondary">
          <table className="w-full text-sm text-theme-text">
            <thead>
              <tr className="bg-theme-secondary">
                <th className="px-4 py-3 text-left font-medium text-theme-text opacity-80" />
                <th className="px-4 py-3 text-right font-medium text-theme-text">Creators</th>
                <th className="px-4 py-3 text-right font-medium text-theme-text">Videos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-secondary">
              <tr className="bg-theme-main">
                <td className="px-4 py-2.5 text-theme-text opacity-90">Possible</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("possible_creators")}`}>{stats.possible_creators}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("possible_videos")}`}>{stats.possible_videos}</td>
              </tr>
              <tr className="bg-theme-main">
                <td className="px-4 py-2.5 text-theme-text opacity-90">Processed</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("processed_creators")}`}>{stats.processed_creators}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("processed_videos")}`}>{stats.processed_videos}</td>
              </tr>
              <tr className="bg-theme-main">
                <td className="px-4 py-2.5 text-theme-text opacity-90">Scored</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("scored_creators")}`}>{stats.scored_creators}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("scored_videos")}`}>{stats.scored_videos}</td>
              </tr>
              <tr className="bg-theme-main">
                <td className="px-4 py-2.5 text-theme-text opacity-90">High Fit</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("highfit_creators")}`}>{stats.highfit_creators}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${fc("highfit_videos")}`}>{stats.highfit_videos}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Error Message */}
      {hasError && status?.error_message && (
        <div className="mt-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text mb-4">
          {status.error_message}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-3">
        {canViewPartial && (
          <Link
            to={audienceUrl}
            onClick={invalidateAudiencesListCache}
            className="px-4 py-2 border border-theme-secondary rounded-md text-theme-text hover:bg-theme-secondary hover:opacity-50"
          >
            View Partial Results
          </Link>
        )}
        {canViewFull ? (
          <Link
            to={audienceUrl}
            onClick={invalidateAudiencesListCache}
            className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
          >
            View Audience
          </Link>
        ) : (
          <span className="px-4 py-2 bg-theme-primary text-theme-button rounded-md opacity-50 cursor-not-allowed">
            View Audience
          </span>
        )}
      </div>
    </>
  );
};

export default PopulatingModal;
